// Static blog content — swap for a Mongo-backed CMS later.
// Structure follows schema.org BlogPosting for rich results + LLM citations.

export const BLOG_POSTS = [
  {
    slug: "modular-kitchen-cost-kolkata-2026",
    title: "Modular kitchen cost in Kolkata (2026): the transparent breakdown",
    excerpt: "How much does a modular kitchen really cost in Kolkata? Layout by layout, material by material, we break down the exact price ranges — and what drives the difference between a ₹1.8 L and a ₹6.5 L kitchen.",
    hero: "https://customer-assets.emergentagent.com/job_zebo-interiors/artifacts/i7g9v5pi_kitchen-mood.webp",
    author: "ZEBO Design Team",
    category: "Pricing",
    tags: ["modular kitchen", "kolkata", "pricing", "buying guide"],
    date: "2026-08-14",
    readMins: 6,
    body: `
### The honest range — ₹1.8 L to ₹6.5 L for a standard 3 BHK reference kitchen

Ask any Kolkata interior firm "what does a modular kitchen cost?" and you will usually get a shrug and a promise to call back after a site visit. That's the wrong answer. A modular kitchen is a factory-made product with well-understood inputs — you can, and should, get an honest range before signing anything.

At ZEBO India Private Limited, the range for a standard 3 BHK reference kitchen (10 – 14 running feet of cabinets, one bank of wall units, single countertop, backsplash) is **₹1.8 Lakh to ₹6.5 Lakh**. Where you land inside that range is a function of four decisions.

### 1. Layout — L, U, Parallel or Island

- **L-shape** — the workhorse. Best value per running foot. Typical Kolkata flats (Rajdanga, Kasba, New Town 2 BHKs) are perfect for this.
- **U-shape** — 20 – 30% more running feet than L, so ₹40 – 80 K more expensive. Great for large family kitchens.
- **Parallel** — two runs facing each other. Adds ~₹30 K over L for the extra countertop and cabinets.
- **Island** — the premium option. Adds ₹80 K – ₹1.5 L for the island cabinetry, additional countertop and often a hood.

### 2. Core material

- **BWR plywood** — the industry default. 710-grade or better. ₹95 – 125 / sqft carcass rate. Excellent for Kolkata's humidity.
- **HDHMR** — dense, screw-holding, moisture-resistant. ₹135 – 165 / sqft. What we recommend for the sink cabinet even in a BWR kitchen.
- **Marine ply** — top tier. ₹185 – 220 / sqft. Overkill for most homes; makes sense in ground-floor / lake-facing flats.

### 3. Shutter finish

- **Laminate** — ₹110 – 180 / sqft finished. Robust, affordable, huge design catalogue.
- **Acrylic** — ₹280 – 380 / sqft. High-gloss, scratch-resistant. Reads modern-Indian.
- **PU lacquer** — ₹380 – 550 / sqft. Solid colour, factory-sprayed. Feels European.
- **Wood veneer** — ₹450 – 700 / sqft. Real wood grain. Ages beautifully.

### 4. Countertop

- **Kashmir granite** — ₹280 – 380 / sqft. Bulletproof, affordable.
- **Quartz** (Caesarstone / Kalinga) — ₹520 – 850 / sqft. Consistent look, hygienic.
- **Italian marble** (Statuario / Calacatta) — ₹1,200 – 2,400 / sqft. Peak luxury.
- **DuPont Corian** — ₹1,400 – 1,900 / sqft. Seamless, thermoformable.

### Add it up

A safe rule of thumb for a 12 ft L-shape kitchen in Kolkata: **BWR core + laminate + Kashmir granite ≈ ₹1.9 – 2.4 L** all-in with basic hardware; **HDHMR + acrylic + quartz ≈ ₹3.4 – 4.2 L**; **HDHMR + PU lacquer + Italian marble + Blum hardware ≈ ₹5.6 – 6.5 L**.

You can price this yourself, live, in the [ZEBO 3D Configurator](/configurator) — swap materials, resize the room, watch the number update in real time.

### What's usually excluded (and how to catch it)

Cheap Kolkata quotes typically hide these behind an asterisk:
- Handles / knobs (₹8 – 30 K)
- Soft-close hardware upgrades (Hettich Silent System is usually ₹18 – 25 K on top)
- Sink & faucet (₹15 – 60 K)
- Chimney & hob (₹35 – 90 K)
- Water purifier point + electrical roughing-in
- Backsplash tiling / stone (₹18 – 45 K)

At ZEBO we include the first three in our BOQ by default. The chimney + hob is your choice of brand — we install what you buy, or we source at cost.

### Book a free site visit

We publish these numbers because we win the transparency argument. If you want an exact price for your kitchen, book a [free site visit](/consultation) — we'll measure, hand you a photorealistic 3D and a fully-priced BOQ within one working day.
`.trim(),
  },
  {
    slug: "2bhk-vs-3bhk-interior-package-what-you-actually-get",
    title: "2 BHK vs 3 BHK interior package in Kolkata — what you actually get",
    excerpt: "Firms advertise 2 BHK packages from ₹4 L and 3 BHK from ₹8 L. What's actually in those boxes? We open up a ZEBO 2 BHK ₹9 L and 3 BHK ₹14 L side by side.",
    hero: "https://customer-assets.emergentagent.com/job_zebo-interiors/artifacts/wibs1lgg_living-mood.webp",
    author: "ZEBO Design Team",
    category: "Buying guides",
    tags: ["2 bhk", "3 bhk", "packages", "kolkata"],
    date: "2026-07-28",
    readMins: 8,
    body: `
### The problem with "starting-price" advertising

Almost every interior firm in Kolkata advertises a suspiciously low starting price. "2 BHK from ₹3.99 L!" "3 BHK from ₹6.99 L!" The number is designed to get you to walk in — and once you're in, the estimator you meet either strips scope until the number is real (paper-thin ply, laminate everywhere, no false ceiling) or piles on "extras" until the real number is 2× the sticker.

ZEBO does the opposite. We publish the real bands — 2 BHK ₹6.5 – 14 L, 3 BHK ₹10 – 22 L — and we spend the consultation matching your scope to a real number in that band.

### What's in a ZEBO 2 BHK ₹9 Lakh package (mid-range)

- Modular kitchen — 10-12 rft, HDHMR carcass, acrylic shutters, Kashmir granite counter, Hettich hardware, chimney provision
- Two wardrobes — sliding, 6 ft each, HDHMR carcass, laminate + wood-veneer feature panel
- False ceiling — living, master bedroom, second bedroom (POP + peripheral cove)
- Lighting — full package (profile lights, downlighters, decorative pendant over dining)
- TV unit — 8 ft, integrated storage
- Study / crockery unit — one, per room preference
- Wall painting — full flat, Asian Paints Royale Luxury
- Electrical — new switches (Legrand Myrius), extra points as required
- Plumbing — kitchen only (no bathroom rework)
- Foyer / entry cabinet — small

### What's in a ZEBO 3 BHK ₹14 Lakh package (mid-range)

Everything above **plus**:
- One additional wardrobe (third bedroom)
- Dining unit or crockery
- Study or home-office joinery
- Bigger TV wall with veneer feature
- False ceiling in the third bedroom
- Slightly upgraded hardware tier

### What's NOT included in either

- Civil work — knocking walls, changing floor tiles, bathroom renovation
- Furniture that isn't built-in (dining table, sofa, beds, chairs)
- Curtains / soft furnishings
- Appliances — chimney, hob, oven, refrigerator, washing machine

We can add any of these to the BOQ; they're just priced separately so you know what's what.

### When the ₹22 L 3 BHK band applies

- Full civil rework (bathroom + kitchen plumbing + floor tile replacement)
- PU-lacquer or wood-veneer cabinetry throughout
- Italian marble or Corian countertops
- Home automation (Legrand / Schneider smart switches, motorised curtains)
- Premium wardrobe internals (Hettich InnoTech Atira drawers)
- Feature ceiling with wooden slats or brass inlays

### The ZEBO promise

Every ZEBO project comes with the [10 · 5 · 1 warranty](/pricing), a named senior designer, a project manager, a photorealistic 3D + 360° render before manufacturing starts, and a written handover timeline.

Ready to see your home? [Book a free consultation](/consultation) — we'll come, measure, and give you a real BOQ inside 24 hours.
`.trim(),
  },
  {
    slug: "how-to-choose-modular-kitchen-material-kolkata-humidity",
    title: "How to choose modular kitchen material for Kolkata's humidity",
    excerpt: "Kolkata's summer humidity chews through cheap plywood in three monsoons. Here's how to choose a core material that lasts a decade — and what to demand from your interior firm.",
    hero: "https://customer-assets.emergentagent.com/job_zebo-interiors/artifacts/2yaczbjm_kitchen-detail.webp",
    author: "ZEBO Design Team",
    category: "Materials",
    tags: ["plywood", "hdhmr", "materials", "kolkata"],
    date: "2026-07-10",
    readMins: 5,
    body: `
### Kolkata is a plywood torture test

Between April and September Kolkata sits between 78 – 92% relative humidity. That water gets into every uncoated ply edge, swells the wood fibres, and blows the laminate off in patches. It's the single biggest reason kitchens in Kolkata die at year four or five when they should live to year ten.

### Three cores, one right answer

**MR ply (moisture-resistant)** — ₹58 – 78 / sqft. **Do not use in the kitchen.** MR ply is a bedroom-wardrobe grade. It handles occasional dampness, not the daily heat + steam of a sink cabinet.

**BWR ply (boiling-water-resistant)** — ₹95 – 125 / sqft, IS-303 grade or better. **The industry default.** Handles Kolkata humidity for a decade if the edges are properly banded. Insist on ISI-marked ply with a manufacturer certificate. Avoid unbranded local mills — the chemistry is inconsistent.

**HDHMR (High-Density Highly-Moisture-Resistant fibreboard)** — ₹135 – 165 / sqft. **The upgrade we recommend for the sink cabinet.** Denser than BWR, holds screws forever, doesn't swell when it gets wet. Slightly heavier so cabinets feel solid.

**Marine ply** — ₹185 – 220 / sqft, BS-1088 grade. **Overkill for most homes.** Makes sense in ground-floor flats near water bodies (Lake Gardens, Salt Lake Sector V) or where you plan to keep the kitchen 20+ years.

### The three edge-band demand

Whichever core you choose, demand this: **every single cut edge — top, bottom, sides, back — must be edge-banded with 2 mm PVC.** Not 0.4 mm foil, which lifts in year two. Not "we'll do the visible edges only." Every cut. The un-banded edge is what fails first, always.

ZEBO uses 2 mm PVC edge-banding by default and includes it in every quote — it costs us maybe ₹6,000 more per kitchen and saves you a decade of frustration.

### Hardware for humidity

- Hinges — go stainless steel (Hettich Sensys or Blum ClipTop). Steel-plated hinges will pit and squeak by year three.
- Channels — telescopic ball-bearing (Hettich Quadro V6 or Blum Tandem). Roller channels get sticky in humid weather.
- Handles — brass or stainless steel. Powder-coated aluminium chips.
- Bottle-pull / corner-carousel — Hettich or Ebco with a lifetime service commitment from your firm.

### The finish decision

- **Laminate** — most humidity-tolerant. Modern high-pressure laminates (Merino, Century, Greenlam) are essentially plastic sheets — humidity doesn't touch them.
- **Acrylic** — 5 mm acrylic on an HDHMR substrate is fine. Cheap 2 mm acrylic on MR ply is a disaster.
- **PU lacquer** — factory-sprayed only. Site-sprayed lacquer in Kolkata monsoon will orange-peel. Insist on a certified PU booth.
- **Wood veneer** — protected by PU top-coat. Absolutely fine for Kolkata as long as it's factory-finished.

### The ZEBO defaults

Our default 3 BHK reference kitchen uses **HDHMR core in the base + sink cabinet, BWR ply in the wall units** (cheaper, and wall units don't face the same humidity load), **2 mm PVC edge-banding everywhere**, **Hettich stainless-steel hinges**, and **Hettich Quadro V6 drawer channels**. This mix costs ~15% more than an all-BWR kitchen and lasts twice as long.

Want to see it in your kitchen? [Design in 3D →](/configurator)
`.trim(),
  },
];

export const CATEGORIES = ["All", "Pricing", "Buying guides", "Materials", "Case studies"];
